import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  useMediaQuery
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import AddCircle from '@mui/icons-material/AddCircle';
import { toast } from 'react-toastify';

import { CustomButton, HumbeeTabs } from '../../../v2/MUI_custom_component';
import BillingInfo from '../../Components/BillingInfo';
import ProductCard from '../../Inventory/components/ProductCard';
import IllustrationWrapper from '../../Illustrration/IllustrationWrapper';
import EmptyCartSvg from '../../Illustrration/components/EmptyCartSvg';
import { decodeToken, deliveryTypeDropDown, OperationType } from '../../../utils/settings';
import { removeProduct, updateProductQty, selectShipToParty, resetSelectedShipToParty, removeShipToParty, setActiveTabIndex } from './reducer/DpActions';
import { selectShipToPartyCarts, selectSelectedShipToPartyId, selectSelectedCategory, selectSelectedFormType, selectDpRouteSource, selectShowPriceChange, selectPoOpenQty, selectTotalDPQuantity, selectMOQ } from './reducer/DpSelectors';
import { GetCategoryProductsAction, getOrderCostAction, ModifyOrderAction } from './API/Action';
import { DP_ROUTE_SOURCE } from './Enums';
import IconReusable from '../../../components/IconReusable';
import { toCapitalized } from '../../../v2/Utils/Capitalize';
import HumbeeModal from '../../../v2/MUI_custom_component/Modal/HumbeeModal';
import DisclaimerSvg from '../../Illustrration/components/DisclaimerSvg';
import DeleteSvg from '../../Illustrration/components/DeleteSvg';
import { buildModifyOrderPayload, buildProductInfo } from './../ModifyOrder'

const OrderSummary = () => {
  const isMobile = useMediaQuery('(max-width: 1200px)');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedSupplier: selectedManufacturer, selectedEntity: selectedCompanyEntity } = useSelector(
      (state) => state.entityReducer
  );
  const showHistory = useSelector(state => state.entityReducer.show_history);
  const token = localStorage.getItem('token');
  const [tokenData, setTokenData] = useState(null);
  useEffect(() => {
      const getTokenData = async () => {
        if (token) {
          try {
            const decoded = await decodeToken(token);
            setTokenData(decoded);
          } catch (err) {
            console.error('Failed to decode token', err);
          }
        }
      };
      getTokenData();
    }, [token]);
  const stpAction = location.state?.stpAction ;
  const selectedGrade = useSelector(selectSelectedCategory)
  const selectedFormType = useSelector(selectSelectedFormType)
  const { order_booking_id, order_id } = useSelector((state) => {
    return {
      order_id: state.dpReducer?.order_id,
      order_booking_id: state.dpReducer?.order_booking_id,
    };
  });
  // const order_id = location.state?.order_id || order_id;
  const showPriceChange = useSelector(selectShowPriceChange);
  const updatedPricingGeo = location.state?.updatedPricingGeo || null;
  const updatePricingGeoId = updatedPricingGeo ? updatedPricingGeo.value : null
  const [ ShowSTPModal, setShowSTPModal ] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shipToPartyToDelete, setShipToPartyToDelete] = useState(null);
  const [loading, setLoading] = useState(false)
  const [pendingSTPAction, setPendingSTPAction] = useState(null);

  const roundToThreeDecimals = (num) => {
    return Math.round((num + Number.EPSILON) * 1000) / 1000
  }

  const getProductQuantity = (productId, cart) => {
    const product = cart.find(p => p.product_id === productId)
    return product ? product.quantity.quantity_in_metric_ton : 0
  }

  // const getAllShipToPartiesProductQuantity = (productId) => {
  //   let totalQty = 0
  //   Object.values(shipToPartyCarts).forEach(cartData => {
  //     if (cartData?.cart) {
  //       const product = cartData.cart.find(p => p.product_id === productId)
  //       if (product) {
  //         totalQty += product.quantity.quantity_in_metric_ton || 0
  //       }
  //     }
  //   })
  //   return totalQty
  // }

  const getMaxQuantityForProduct = (productId, cart) => {
    if (!poOpenQty || poOpenQty <= 0) {
      return 0
    }
    
    const currentQty = getProductQuantity(productId, cart)
    const remainingPOQty = Math.max(0, poOpenQty - totalDPQuantity)
    
    let maxAllowableQty
    if (selectMOQData?.max_order_quantity && typeof selectMOQData.max_order_quantity === 'number' && selectMOQData.max_order_quantity > 0) {
      // Use remaining DP capacity based on max_order_quantity
      const remainingDPQty = Math.max(0, selectMOQData.max_order_quantity - totalDPQuantity)
      // Take intersection of PO remaining and DP remaining capacity
      maxAllowableQty = Math.min(remainingPOQty, remainingDPQty)
    } else {
      // If no DP limit defined, just use PO limit
      maxAllowableQty = remainingPOQty
    }
    
    return roundToThreeDecimals(currentQty + maxAllowableQty)
  }

  const validatePOLimit = (productId, newQty, cart, targetShipToPartyId) => {
    if (!poOpenQty || poOpenQty <= 0) {
      toast.error('No PO open quantity limit available')
      return false
    }

    const currentQtyInThisCart = getProductQuantity(productId, cart)
    const qtyIncrease = newQty - currentQtyInThisCart
    
    if (qtyIncrease <= 0) {
      return true
    }

    const newTotalQty = totalDPQuantity + qtyIncrease
    const remainingPOQty = Math.max(0, poOpenQty - totalDPQuantity)
    const remainingDPQty = (selectMOQData?.max_order_quantity && typeof selectMOQData.max_order_quantity === 'number' && selectMOQData.max_order_quantity > 0) 
      ? Math.max(0, selectMOQData.max_order_quantity - totalDPQuantity) 
      : Infinity
    
    // Check both PO and DP limits simultaneously - allow minimum of both
    const isPOLimitExceeded = newTotalQty > poOpenQty
    const isDPLimitExceeded = (selectMOQData?.max_order_quantity && typeof selectMOQData.max_order_quantity === 'number' && selectMOQData.max_order_quantity > 0) 
      && newTotalQty > selectMOQData.max_order_quantity
    
    if (isPOLimitExceeded || isDPLimitExceeded) {
      // Determine which limit is more restrictive
      const maxAllowedPO = roundToThreeDecimals(currentQtyInThisCart + remainingPOQty)
      const maxAllowedDP = (selectMOQData?.max_order_quantity && typeof selectMOQData.max_order_quantity === 'number' && selectMOQData.max_order_quantity > 0) 
        ? roundToThreeDecimals(currentQtyInThisCart + remainingDPQty) 
        : Infinity
      
      const finalMaxAllowed = Math.min(maxAllowedPO, maxAllowedDP)
      
      if (finalMaxAllowed <= currentQtyInThisCart) {
        if (remainingDPQty <= remainingPOQty) {
          toast.error(`DP quantity limit of ${roundToThreeDecimals(selectMOQData.max_order_quantity)} has been reached. Cannot add more items.`)
        } else {
          toast.error(`PO quantity limit of ${roundToThreeDecimals(poOpenQty)} has been reached. Cannot add more items.`)
        }
        return false
      } else {
        if (remainingDPQty <= remainingPOQty) {
          toast.error(`DP Limit reached. Maximum allowed for this item: ${finalMaxAllowed} ton`)
        } else {
          toast.error(`PO Limit reached. Maximum allowed for this item: ${finalMaxAllowed} ton`)
        }
        return finalMaxAllowed
      }
    }
    
    return true
  }
  const fetchProducts = async (updatePricingGeoId) => {
    if (!updatePricingGeoId || !selectedCompanyEntity) return
    const payload = { 
      sku_value_ids: [
        Number(selectedGrade),
        Number(selectedFormType),
      ],
      manufacturer_pricing_geography_id: updatePricingGeoId,
      order_booking_id: order_booking_id || null,
      company_esi_id: selectedCompanyEntity,
    }
    const {data, error, message } = await GetCategoryProductsAction(payload)
    if (!error) {
      updateCartItemsPrices(data)
    }else{
      toast.error(message)
    }
  }
  const updateCartItemsPrices = (newProductList) => {
    if (!newProductList?.length) return;

    Object.entries(shipToPartyCarts).forEach(([shipToPartyId, cartData]) => {
      if (!cartData?.cart?.length) return;

      cartData.cart.forEach(cartItem => {
        const updatedProduct = newProductList.find(
          p => p.product_id === cartItem.product_id
        );

        if (
          updatedProduct &&
          Number(updatedProduct.current_price_per_unit) !==
            Number(cartItem.current_price_per_unit)
        ) {
          dispatch({
            type: 'UPDATE_SHIP_TO_PARTY_PRODUCT_PRICE',
            data: {
              shipToPartyId,
              productId: cartItem.product_id,
              newPrice: Number(updatedProduct.current_price_per_unit)
            }
          });
        }
      });
    });
  };

  useEffect(() => {
    if (updatePricingGeoId && selectedCompanyEntity) {
      fetchProducts(updatePricingGeoId)
    }
  }, [updatePricingGeoId, selectedCompanyEntity])

  const shipToPartyCarts = useSelector(selectShipToPartyCarts);
  const selectedShipToPartyId = useSelector(selectSelectedShipToPartyId);
  const poOpenQty = useSelector(selectPoOpenQty);
  const totalDPQuantity = useSelector(selectTotalDPQuantity);
  const selectMOQData = useSelector(selectMOQ);
  const routeSource = useSelector(selectDpRouteSource);
  const shipToPartyList = useMemo(() => {
    const list = Object.entries(shipToPartyCarts).map(
      ([shipToPartyId, data]) => {
        const hasHumbeeSOId = data.shipToPartyDetails?.humbee_so_id;
        const isDisabled = routeSource === DP_ROUTE_SOURCE.DP_DETAILS && hasHumbeeSOId;
        
        return {
          shipToPartyId,
          cart: data.cart || [],
          totals: data.totals || {},
          shipToPartyDetails: data.shipToPartyDetails || {},
          activeTabIndex: data.activeTabIndex || 0,
          isAddTab: false,
          isDisabled
        };
      }
    )
    .sort((a, b) => (a.activeTabIndex || 0) - (b.activeTabIndex || 0));
    
    if (list.length < 3) {
      list.push({ isAddTab: true });
    }

    return list;
  }, [shipToPartyCarts, routeSource]);

  const [currentTab, setCurrentTab] = useState(0);
  
  // Effect to handle tab selection when coming from DP Details
  useEffect(() => {
    if (routeSource === DP_ROUTE_SOURCE.DP_DETAILS && shipToPartyList.length > 0) {
      const stpsOnly = shipToPartyList.filter(item => !item.isAddTab);
      if (stpsOnly.length > 0) {
        // Find STP with highest activeTabIndex (newest)
        const newestStp = stpsOnly.reduce((max, current) => {
          return (current.activeTabIndex || 0) > (max.activeTabIndex || 0) ? current : max;
        });
        
        // Find the index of newest STP in the filtered/sorted list
        const newestStpIndex = stpsOnly.findIndex(stp => stp.shipToPartyId === newestStp.shipToPartyId);
        
        if (newestStpIndex !== -1 && newestStpIndex !== currentTab) {
          setCurrentTab(newestStpIndex);
          dispatch(selectShipToParty(newestStp.shipToPartyId));
        }
      }
    }
  }, [shipToPartyList, routeSource, dispatch]);

  // Effect to handle default tab selection for other routes
  useEffect(() => {
    if (
      routeSource !== DP_ROUTE_SOURCE.DP_DETAILS &&
      shipToPartyList.length > 0 &&
      !shipToPartyList[currentTab]?.isAddTab
    ) { 
      // If there's a selectedShipToPartyId, find and select its tab
      if (selectedShipToPartyId) {
        const selectedStpIndex = shipToPartyList.findIndex(item => 
          !item.isAddTab && item.shipToPartyId === selectedShipToPartyId
        );
        if (selectedStpIndex !== -1 && selectedStpIndex !== currentTab) {
          setCurrentTab(selectedStpIndex);
        }
      } else {
        // No selected STP, select the first one
        dispatch(selectShipToParty(shipToPartyList[0].shipToPartyId));
        setCurrentTab(0);
      }
    }
  }, [shipToPartyList, selectedShipToPartyId, dispatch, routeSource, currentTab]);
  const getDeliveryTypeLabel = (value) => {
    const type = deliveryTypeDropDown.find(t => t.value === value);
    return type ? type.label : '-';
  };
  const formatDeliveryTypeLabel = (value) => {
    const label = getDeliveryTypeLabel(value);
    return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
  };
  const handleAddProducts = () => {
    navigate('/create-dp', {
      state: {
        from: DP_ROUTE_SOURCE.ORDER_SUMMARY,
        shipToPartyId: selectedShipToPartyId,
        order_booking_id: location.state?.order_booking_id,
      }
    });
  };
  const handleAddShipToParty = () => {
    dispatch(resetSelectedShipToParty());

    navigate('/create-dp', {
      state: {
        isNewShipToParty: true,
        order_booking_id: location.state?.order_booking_id,
      }
    });
  };

  const handleConfirmAndProceed = () => {
    if (stpAction === OperationType.ADD) {
      setPendingSTPAction(OperationType.ADD);
      setShowSTPModal(true);
      return;
    }
    
    selectedManufacturer && getOrderSummary();
  };

  const safeCapitalize = (text) => {
    return text && typeof text === 'string' ? toCapitalized(text) : text;
  };
  const getOrderSummary = async () => {
    const orderProductsDelivery = Object.entries(shipToPartyCarts).map(
      ([shipToPartyId, stpData]) => {
        const {
          cart,
          shipToPartyDetails
        } = stpData;
        return {
          ship_to_party_info: {
            so_id: 0,
            company_erp_code: shipToPartyDetails?.selected_esi_info.external_system_code,
            deliver_to: shipToPartyDetails.company_id,
            deliver_to_esi_id: shipToPartyDetails?.selected_esi_info.esi_id,
            company_name: shipToPartyDetails.company_name || '',
            company_gstin: shipToPartyDetails.gstin || '',
            company_pan: shipToPartyDetails.pancard_no || '',
            company_phone_no: shipToPartyDetails.company_phone_no || '',
            company_registered_address: [
              safeCapitalize(shipToPartyDetails?.company_address1),
              safeCapitalize(shipToPartyDetails?.company_address2),
              safeCapitalize(shipToPartyDetails?.company_address3),
              safeCapitalize(shipToPartyDetails?.location_name),
              safeCapitalize(shipToPartyDetails?.district_name),
              safeCapitalize(shipToPartyDetails?.state_name),
              safeCapitalize(shipToPartyDetails?.pincode),
            ]
              .filter(Boolean)
              .join(', ')
            ,
            delivery_address: {
              address_line_1: shipToPartyDetails.delivery_Address_line1 || '',
              address_line_2: shipToPartyDetails.delivery_Address_line2 || '',
              address_line_3: shipToPartyDetails.delivery_Address_line3 || '',
            },
            delivery_pincode: shipToPartyDetails.delivery_company_pincode,
            delivery_district_id: shipToPartyDetails.delivery_district_id || null,
            delivery_district_name: shipToPartyDetails.delivery_district_name || null,
            delivery_state_id: shipToPartyDetails.delivery_state_id || null,
            delivery_state_name: shipToPartyDetails.delivery_state_name || null,
            location_id: shipToPartyDetails.delivery_location_id || null,
            location_name: shipToPartyDetails.delivery_location_name || '',
            pricing_geography_id: shipToPartyDetails.pricing_geography_id || null,
            pricing_geography_name: shipToPartyDetails.pricing_geography_name || null,
            delivery_type: shipToPartyDetails.delivery_type || null,
            delivery_type_name: getDeliveryTypeLabel(shipToPartyDetails.delivery_type),
            order_delivery_contact_no: shipToPartyDetails.receiver_mobile || '',
            order_delivery_contact_person_name: shipToPartyDetails.receiver_name || '',
          },

          products_info: cart.map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            product_logo: item.product_image,
            price_info: {
              landing_price: item.current_price_per_unit,
            },
            quantity: {
              quantity_in_bags: item.quantity.quantity_in_bags,
              quantity_in_metric_ton: item.quantity.quantity_in_metric_ton,
              multiplier: item.quantity.multiplier,
              unit: item.quantity.unit,
              base_unit: item.quantity.base_unit,
            },
            form_type: item.form_type ?? 1,
            bundle: item.bundle ?? 0,
            ordered_quantity: null,
            dispatched_quantity: null,
            remaining_quantity: null,
            
          }))
        };
      }
    );
    const payload = {
      order_products_delivery: orderProductsDelivery,
      supplier_id: selectedManufacturer,
      buyer_id: tokenData.default_company_info.company_id,
      buyer_esi_id: selectedCompanyEntity,
      seller_id: selectedManufacturer,
      order_booking_id
    };
    setLoading(true);
    const { data, error, message } = await getOrderCostAction(payload);
    setLoading(false);
    if (!error) {
      navigate('/dp-checkout', { state: { checkOutData: data } });
    } else {
      toast.error(message);
    }
  };
  const renderCartItems = (cartItems, shipToPartyId, isDisabled = false) => {
    if (!cartItems.length) {
      return (
        <IllustrationWrapper
          illustration={<EmptyCartSvg />}
          title="Cart Empty!"
          subtitle="Add products to cart to proceed"
        />
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {cartItems.map(item => (
          <ProductCard
            key={item.product_id}
            productName={item.product_name}
            imageUrl={item.product_image}
            price={item.current_price_per_unit}
            quantity={item.quantity.quantity_in_metric_ton}
            maxQuantity={getMaxQuantityForProduct(item.product_id, cartItems)}
            showQuantityControls={!isDisabled}
            showDeleteButton={!isDisabled}
            onIncreasePress={isDisabled ? undefined : () => {
              const newQty = item.quantity.quantity_in_metric_ton + 1
              const validation = validatePOLimit(item.product_id, newQty, cartItems, shipToPartyId)
              if (validation !== false) {
                const finalQty = typeof validation === 'number' ? validation : newQty
                dispatch(updateProductQty(item.product_id, finalQty, shipToPartyId))
              }
            }}
            onReducePress={isDisabled ? undefined : () => {
              const newQty = item.quantity.quantity_in_metric_ton - 1
              if (newQty <= 0) {
                dispatch(removeProduct(item.product_id, shipToPartyId))
              } else {
                dispatch(updateProductQty(item.product_id, newQty, shipToPartyId))
              }
            }}
            onDelete={isDisabled ? undefined : () =>
              dispatch(removeProduct(item.product_id, shipToPartyId))
            }
          />
        ))}
      </Box>
    );
  };

  const handleRemoveShipToParty = (shipToPartyId) => {
    setShipToPartyToDelete(shipToPartyId);
    setShowDeleteModal(true);
  };

  const confirmDeleteShipToParty = () => {
    if (!shipToPartyToDelete) return;
    
    // Find the index of the current tab being deleted
    const deletingTabIndex = shipToPartyList.findIndex(item => item.shipToPartyId === shipToPartyToDelete);
    
    dispatch(removeShipToParty(shipToPartyToDelete));
    
    // If deleting the 2nd or 3rd tab, select the first tab
    if (deletingTabIndex > 0) {
      setCurrentTab(0);
      const firstShipToParty = shipToPartyList[0];
      if (firstShipToParty && !firstShipToParty.isAddTab) {
        dispatch(selectShipToParty(firstShipToParty.shipToPartyId));
      }
    }
    
    // Reset modal states
    setShowDeleteModal(false);
    setShipToPartyToDelete(null);
  }
  
  const AddShiptoParty = async () => {
    setShowSTPModal(false);

    // Filter out all new Ship-To-Parties without a humbee_so_id
    const newShipToParties = Object.entries(shipToPartyCarts).filter(
      ([, data]) => !data?.shipToPartyDetails?.humbee_so_id
    );

    if (!newShipToParties.length) {
      toast.error('No new Ship to Parties found');
      return;
    }

    // Validate all STPs have products before processing
    const invalidSTPs = newShipToParties.filter(([, stpData]) => !stpData.cart?.length);
    if (invalidSTPs.length > 0) {
      toast.error('All Ship to Parties must have at least one product');
      return;
    }
    // Build shipments array for batch processing
    const shipments = newShipToParties.map(([, stpData]) => {
      const { cart, shipToPartyDetails } = stpData;

      const shipment = {
        ship_to_party_info: {
          humbee_so_id: null,
          company_info: {
            id: shipToPartyDetails?.company_id,
            esi_id: shipToPartyDetails?.selected_esi_id,
            external_system_code: shipToPartyDetails?.selected_esi_info?.external_system_code,
            name: shipToPartyDetails?.company_name,
            phone: shipToPartyDetails?.company_phone_no,
            gst: shipToPartyDetails?.gstin,
          },
          address_info: {
            address: {
              address_line_1: shipToPartyDetails?.delivery_Address_line1,
              address_line_2: shipToPartyDetails?.delivery_Address_line2,
              address_line_3: shipToPartyDetails?.delivery_Address_line3,
            },
            pincode: shipToPartyDetails?.delivery_company_pincode,
            district_info: {
              id: shipToPartyDetails?.delivery_district_id,
              name: shipToPartyDetails?.delivery_district_name,
            },
            state_info: {
              id: shipToPartyDetails?.delivery_state_id,
              name: shipToPartyDetails?.delivery_state_name,
            },
            location_info: {
              location_id: shipToPartyDetails?.delivery_location_id,
              location_name: shipToPartyDetails?.delivery_location_name,
            },
          },
          receiver_info: {
            name: shipToPartyDetails?.receiver_name ?? '',
            phone_number: shipToPartyDetails?.receiver_mobile,
          },
          delivery_info: {
            delivery_type: shipToPartyDetails?.delivery_type,
            incoterms: shipToPartyDetails?.incoterms ?? 2,
          },
          pricing_geography_info: {
            pricing_geography_id: shipToPartyDetails?.pricing_geography_id,
            pricing_geography_name: shipToPartyDetails?.pricing_geography_name,
          },
        },
      };

      const products = cart.map((product) =>
        buildProductInfo(product, OperationType.ADD)
      );

      return {
        shipment,
        products,
        operation: OperationType.ADD
      };
    });

    // Create batch payload using the updated function
    const batchPayload = buildModifyOrderPayload({
      orderId: order_id,
      shipments: shipments,
    });

    setLoading(true);
    try {
      const { error, message } = await ModifyOrderAction(batchPayload, selectedCompanyEntity);
      setLoading(false);

      if (!error) {
        toast.success(
          `Ship to Parties added successfully`
        );
        navigate(`/dispatch-plan-detail/${order_id}`);
      } else {
        toast.error(`Failed to add Ship-to-Parties: ${message}`);
      }
    } catch (error) {
      setLoading(false);
      toast.error(`Error processing Ship-to-Parties: ${error.message || 'Unknown error occurred'}`);
    }

    // Navigate to the dispatch plan detail page after processing all STPs
    navigate(`/dispatch-plan-detail/${order_id}`);
  };

  useEffect(() => {
    const isShipToPartyEmpty =
      !shipToPartyCarts || Object.keys(shipToPartyCarts).length === 0;

    if (isShipToPartyEmpty) {
      navigate('/create-dp', {
        state: {
          isNewShipToParty: true,
          order_booking_id: location.state?.order_booking_id,
        }
      });
    }
  }, [shipToPartyCarts]);
  const handleEditShipToParty = (shipToPartyId, shipToPartyData) => {
    const STPCountMore = Object.keys(shipToPartyCarts).length > 1;
    navigate('/dp-shiping-info', {
      state: {
        editMode: true,
        shipToPartyId: shipToPartyId,
        shipToPartyKey: shipToPartyId,
        shipToPartyData: shipToPartyData,
        STPCountMore: STPCountMore,
      }
    })
  }
  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        justifyContent:'end', 
        p: { xs: 2, md: 0 },
        mr: { xs: 0, md: 4 },
        position: {xs: 'fixed', md: 'relative'}, 
        top: { xs: '88px', md: 0 }, 
        left: {xs: 0, md: 'auto'},
        right: {xs: 0, md: 'auto'},
        zIndex: {xs: 1000, md: 'auto'}, 
        zIndex: 1000, 
        boxShadow: {xs: '0px 2px 8px 0px #0000001A', md: 'none'},
        background: {xs: '#FAFAFA', md: 'transparent'},
        pb: 0,
      }}>
        <CustomButton
          variant="filled"
          color="primary"
          label="Continue"
          loading={loading}
          onClick={handleConfirmAndProceed}
          sx={{ width: { xs: '100%', md: 'auto' } }}
        />
      </Box>

      <Box sx={{ mt: { xs: '72px', md: 0 }, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h2">Order Summary</Typography>
        <HumbeeTabs
          tabVariant={isMobile ? 'small' : 'default'}
          data={[...shipToPartyList
            .filter(item => !item.isAddTab)
            .map(stp => ({
              ...stp,
              disabled: stp.isDisabled
            })), ...((shipToPartyList.filter(item => !item.isAddTab).length < 3)  ? [{ isAddTab: true, icon: 'Add', disabled: totalDPQuantity >= poOpenQty || (selectMOQData?.max_order_quantity && typeof selectMOQData.max_order_quantity === 'number' && selectMOQData.max_order_quantity > 0 && totalDPQuantity >= selectMOQData.max_order_quantity) }] : [])]}
          currentTab={currentTab}
          onChange={(e, newValue) => {
            const tab = shipToPartyList[newValue];

            // Prevent interaction with disabled tabs
            if (tab?.isDisabled) {
              return;
            }

            if (tab?.isAddTab) {
              if (tab.disabled || totalDPQuantity >= poOpenQty) {
                return;
              }
              handleAddShipToParty()
              return;
            }

            setCurrentTab(newValue);
            dispatch(selectShipToParty(tab.shipToPartyId));
          }}
          getTabIcon={(shipToParty, index, isSelected) => {
            if (shipToParty.isAddTab || shipToParty.icon) {
              return (
                <IconReusable 
                  name={shipToParty.icon} 
                  size={20} 
                />
              );
            }
            return null;
          }}
          getTabLabel={(shipToParty, index) => {
              if (shipToParty.isAddTab) {
                return 'Add Ship to Party';
              }
              const baseLabel = `Ship to Party ${index + 1}`;
              return shipToParty.isDisabled ? `${baseLabel} ` : baseLabel;
            }
          }
          getTabKey={(shipToParty, index) => 
            shipToParty.isAddTab ? 'add-tab' : shipToParty.shipToPartyId
          }
          renderTabContent={(shipToParty) => {
            if (shipToParty.isAddTab) {
              return null
            }
            const { shipToPartyDetails, cart, totals } = shipToParty;
            const billingInfoAdapter = {
              company_name: shipToPartyDetails.company_name || '—',
              company_phone_no: shipToPartyDetails.company_phone_no || '—',
              selectedRole: shipToPartyDetails.selectedRole,
              company_address1: shipToPartyDetails.company_address1 || '',
              company_address2: shipToPartyDetails.company_address2 || '',
              company_address3: shipToPartyDetails.company_address3 || '',
              district_id: shipToPartyDetails.district_id || null, 
              location_id: shipToPartyDetails.location_id || null,
              state_id: shipToPartyDetails.state_id || null,
              company_pincode: shipToPartyDetails.company_pincode || '',
              erp_code: shipToPartyDetails.selected_esi_info.external_system_code
            };
            return (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ background: '#fff', p: 2, borderRadius: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <BillingInfo
                        buyer={billingInfoAdapter}
                        title="Ship to Party"
                        showRoleChip
                        showDeleteButton={!shipToParty.isDisabled}
                        onDelete={() => handleRemoveShipToParty(shipToParty.shipToPartyId)}
                        orderSummary
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ 
                      p: 2,
                        background: '#fff',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography variant="labelD">Shipping Address</Typography>
                        {!shipToParty.isDisabled && (
                          <IconButton 
                            size="small"
                            onClick={() => handleEditShipToParty(shipToParty.shipToPartyId, shipToPartyDetails)}
                            disabled={showHistory}
                          >
                            <IconReusable name='Edit' color={!showHistory ? '#995A00' : '#E5E5E5' }  size={isMobile ? 16: 24 } />
                          </IconButton>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1}}>
                        <Typography variant="labelM" sx={{ minWidth: '150px', color: '#666' }}>
                          Address
                        </Typography> 
                        <Typography variant="p3">
                          {shipToPartyDetails.delivery_Address_line1}, {shipToPartyDetails.delivery_Address_line2}, {' '}
                          {shipToPartyDetails.delivery_Address_line3}, {shipToPartyDetails.delivery_district_name},  {shipToPartyDetails.delivery_location_name}, {shipToPartyDetails.delivery_state_name}, {shipToPartyDetails.delivery_company_pincode}
                        </Typography>
                      </Box>
                      <Typography variant="labelD" sx={{}}>
                        Logistic Details
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1}}>
                        <Typography variant="labelM" sx={{ minWidth: '150px', color: '#666' }}>
                          Delivery Type
                        </Typography> 
                        <Typography variant="p3">
                          {formatDeliveryTypeLabel(shipToPartyDetails.delivery_type)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1}}>
                        <Typography variant="labelM" sx={{ minWidth: '150px', color: '#666' }}>
                          Receiver’s Name
                        </Typography> 
                        <Typography variant="p3">
                          {shipToPartyDetails.receiver_name || '-'}
                        </Typography> 
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1}}>
                        <Typography variant="labelM" sx={{ minWidth: '150px', color: '#666' }}>
                          Receiver’s Mobile number
                        </Typography> 
                        <Typography variant="p3">
                          {shipToPartyDetails.receiver_mobile || '-'}
                        </Typography> 
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="labelD">Products</Typography>
                  {!shipToParty.isDisabled && (
                    <CustomButton
                      variant="outlined"
                      size="small"
                      label="Add Products"
                      disabled={showHistory}
                      onClick={handleAddProducts}
                    />
                  )}
                </Box>
                {showPriceChange && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconReusable name='InfoOutlined' size='20px' color='#CC0000' />
                      <Typography variant="p3" sx={{ color: '#CC0000'}} >
                        Prices have been changed as per the selected shipping address.
                      </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between'}}>
                  <Typography variant="labelD">
                    Total Quantity: 
                  </Typography>
                  <Typography variant="labelD">
                    {totals.total_quantity_in_metric_ton} {cart[0]?.quantity?.unit}
                  </Typography>
                </Box>

                {renderCartItems(cart, shipToParty.shipToPartyId, shipToParty.isDisabled)}
              </Box>
            );
          }}
        />
      </Box>
      <HumbeeModal
        open={ShowSTPModal}
        onClose={() => {
          setShowSTPModal(false);
          setPendingSTPAction(null);
        }}
        onConfirm={() => {
          if (pendingSTPAction === OperationType.ADD) {
            AddShiptoParty();
          }
        }}
        
        loading={loading}
        title="New Ship to Party added"
        subtitle="Your order will be updated."
        confirmLabel="Update Order"
        cancelLabel="No"
        illustration={<DisclaimerSvg />}
        width={{ xs: 400, md: 600 }}
      />
      
      <HumbeeModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setShipToPartyToDelete(null);
        }}
        onConfirm={confirmDeleteShipToParty}
        title="Are you sure you want to delete Ship to Party?"
        subtitle="This action cannot be undone. All products in this ship to party will be removed."
        confirmLabel="Yes"
        cancelLabel="No"
        illustration={<DeleteSvg />}
        width={{ xs: 400, md: 400 }}
      />
    </>
  );
};

export default OrderSummary;
